import { useState } from 'react';
import { JitsiMeeting } from '@jitsi/react-sdk';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Video, Phone, PhoneOff } from 'lucide-react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function VideoCallButton({ otherUserId, otherUserName }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [showCall, setShowCall] = useState(false);
  const [roomName, setRoomName] = useState(null);
  const [callType, setCallType] = useState('video');

  const startCall = async (type) => {
    try {
      const response = await axios.post(
        `${API}/calls/create-room?other_user_id=${otherUserId}&call_type=${type}`,
        {},
        { withCredentials: true }
      );
      
      setRoomName(response.data.room_name);
      setCallType(type);
      setShowCall(true);
    } catch (error) {
      console.error('Error starting call:', error);
      toast.error(t('errors.generic'));
    }
  };

  const endCall = () => {
    setShowCall(false);
    setRoomName(null);
    toast.info(t('calls.callEnded'));
  };

  return (
    <>
      <div className="flex gap-2">
        <Button
          onClick={() => startCall('video')}
          size="sm"
          variant="outline"
          className="border-teal-500 text-teal-600 hover:bg-teal-50 gap-2"
        >
          <Video className="h-4 w-4" />
          {t('calls.videoCall')}
        </Button>
        <Button
          onClick={() => startCall('voice')}
          size="sm"
          variant="outline"
          className="border-cyan-500 text-cyan-600 hover:bg-cyan-50 gap-2"
        >
          <Phone className="h-4 w-4" />
          {t('calls.voiceCall')}
        </Button>
      </div>

      {/* Call Dialog */}
      <Dialog open={showCall} onOpenChange={setShowCall}>
        <DialogContent className="max-w-6xl h-[80vh] p-0">
          <div className="h-full flex flex-col">
            <div className="bg-teal-500 text-white p-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">
                  {callType === 'video' ? '📹' : '📞'} {t('calls.callingWith')} {otherUserName}
                </h3>
                <p className="text-sm text-teal-100">{callType === 'video' ? t('calls.videoCall') : t('calls.voiceCall')}</p>
              </div>
              <Button
                onClick={endCall}
                size="sm"
                variant="destructive"
                className="bg-red-500 hover:bg-red-600 gap-2"
              >
                <PhoneOff className="h-4 w-4" />
                {t('calls.endCall')}
              </Button>
            </div>
            
            {roomName && (
              <div className="flex-1">
                <JitsiMeeting
                  roomName={roomName}
                  configOverwrite={{
                    startWithAudioMuted: false,
                    startWithVideoMuted: callType === 'voice',
                    disableModeratorIndicator: true,
                    enableWelcomePage: false,
                    prejoinPageEnabled: false,
                  }}
                  interfaceConfigOverwrite={{
                    DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
                    SHOW_JITSI_WATERMARK: false,
                    SHOW_WATERMARK_FOR_GUESTS: false,
                    TOOLBAR_BUTTONS: [
                      'microphone',
                      'camera',
                      'closedcaptions',
                      'desktop',
                      'fullscreen',
                      'fodeviceselection',
                      'hangup',
                      'chat',
                      'settings',
                      'videoquality',
                      'filmstrip',
                      'stats',
                      'tileview'
                    ],
                  }}
                  userInfo={{
                    displayName: user?.name || 'User',
                    email: user?.email || ''
                  }}
                  onReadyToClose={endCall}
                  getIFrameRef={(iframeRef) => {
                    iframeRef.style.height = '100%';
                    iframeRef.style.width = '100%';
                  }}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
